/* eslint-disable react/jsx-pascal-case */
import React from "react";
import Dashboard from "../../pages/dashboard/Dashboard.tsx";
import { Helmet } from "react-helmet-async";
import PATHS from "../../utils/paths";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
} from "@mui/joy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { colors } from "../../customTheme";
import affiliateProgramImage from "../../images/affiliate-hero-image.png";

const AFFILIATE_SIGNUP_URL = "https://affiliates.poshwatch.io/login";
type StepCardProps = {
  step: string;
  title: string;
  children: React.ReactNode;
};

const StepCard = ({ step, title, children }: StepCardProps) => (
  <Card variant="soft" sx={{ bgcolor: "background.level1", borderRadius: 16 }}>
    <CardContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          color: "text.tertiary",
        }}
      >
        <Typography level="body-sm" sx={{ fontWeight: 700, fontSize: { xs: 14, sm: 16, md: 18 } }}>{title}</Typography>
        <Typography level="body-xs">{step}</Typography>
      </Box>
      <Typography level="body-sm" color="neutral">
        {children}
      </Typography>
    </CardContent>
  </Card>
);

const HeroSection = () => {
  return (
    <Box sx={{ py: { xs: 3, md: 5 } }}>
      <Box component="section" sx={{ textAlign: "center", py: { xs: 4, md: 6 } }}>
        <Typography level="h1" sx={{ fontSize: { xs: 24, sm: 32, md: 40 }, mb: 4, fontWeight: 700, fontFamily: "Libre Baskerville" }}>
          Earn 30% Recurring
          <br />
          Commission <span style={{ fontStyle: 'italic' }}>for Life</span>
        </Typography>

        <Typography level="body-lg" color="neutral" sx={{ maxWidth: 900, mx: "auto", mt: 1.5 }}>
          Start earning passive income for every paying customer you refer, without limits.
          <br />
          Join Poshwatch's affiliate program today.
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: "flex",
            gap: 1.25,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            size="md"
            variant="solid"
            sx={{ 
              borderRadius: 12, 
              backgroundColor: colors.slatePurple, 
              color: 'white', 
              fontWeight: 700,
              '&:hover': {
                backgroundColor: colors.slatePurple,
                opacity: 0.9
              }
            }}
            endDecorator={<span aria-hidden>→</span>}
            onClick={() => window.open(AFFILIATE_SIGNUP_URL, "_self")}
          >
            Become an Affiliate
          </Button>
        </Box>
      </Box>

      <Box component="section" sx={{ py: { xs: 2, md: 2 }, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ maxWidth: { xs: '100%', sm: 400, md: 500 }, width: '100%' }}>
          <img 
            src={affiliateProgramImage} 
            alt="Affiliate Program" 
            style={{ 
              width: '100%', 
              height: 'auto', 
              borderRadius: '20px',
              objectFit: 'cover',
              boxShadow: '0 6px 24px rgba(0, 0, 0, 0.12)'
            }} 
          />
        </Box>
      </Box>

      <Box component="section" sx={{ py: { xs: 2, md: 4 }, marginTop: { xs: 2, md: 6 } }}>
        <Typography level="h2" sx={{ textAlign: "center", fontSize: { xs: 24, md: 32 }, mb: 4, fontFamily: "Libre Baskerville" }}>
          How it Works
        </Typography>
        <Typography level="body-lg" color="neutral" sx={{ maxWidth: 900, mx: "auto", mt: 1.5, textAlign: "center", mb: 4 }}>
          Start earning money in less than an hour. Here's how:
        </Typography>
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            maxWidth: { xs: 560, sm: 720, md: 920 }, // <- narrower container
            mx: "auto",                               // <- center it
          }}
        >
          <StepCard step="Step: 1" title="👋 Register">
            Create your free affiliate account. Get instant access to your unique link and dashboard.
          </StepCard>
          <StepCard step="Step: 2" title="👀 Share">
            Share  your link anywhere you post content, on your blog, social media, or with your
            network. Sign ups are tracked to you, and you get 30% for life.
          </StepCard>
          <StepCard step="Step: 3" title="💰 Earn">
            Check in any time to see how many customers you’ve referred and check your payout dates. You can also check your earnings history.
          </StepCard>
        </Box>
      </Box>
    </Box>
  );
};

const WhyJoinSection = () => {
  return (
    <Box component="section" sx={{ py: { xs: 4, md: 6 } }}>
      <Typography
        level="h2"
        sx={{
          textAlign: "center",               
          fontSize: { xs: 24, sm: 32, md: 40 },
          fontWeight: 800,
          mb: 4,
          fontFamily: "Libre Baskerville",
        }}
      >
        Why Join Our Affiliate Program?
      </Typography>

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", md: "auto auto" },
          justifyContent: "center",
          columnGap: 6,
          rowGap: 2,
          width: { xs: "100%", md: "fit-content" },
          mx: "auto",
          mb: 10,
        }}
      >
        {[
          "30% lifetime commission",
          "Recurring revenue",
          "60-day attribution period",
          "Monthly payouts",
        ].map((text) => (
          <Box key={text} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "success.softBg",
                color: "success.solidColor",
                flex: "0 0 26px",
              }}
            >
              <CheckRoundedIcon fontSize="small" />
            </Box>
            <Typography level="body-lg">{text}</Typography>
          </Box>
        ))}
      </Box>

      <Card
        variant="soft"
        sx={{
          width: "100%",
          maxWidth: { xs: 640, sm: 720 }, // was 980
          mx: "auto",
          borderRadius: 18,
          bgcolor: "background.level1",
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Typography level="h3" sx={{ fontSize: { xs: 22, md: 26 }, mb: 3, fontWeight: 700 }}>
            Frequently Asked Questions
          </Typography>

          <Box sx={{ mb: 2.5 }}>
            <Typography level="title-sm" sx={{ fontWeight: 700, mb: 0.5 }}>
              How do I claim my commisions?
            </Typography>
            <Typography level="body-md" color="neutral">
              Your payment details are collected when you sign up. payments are processed monthly for all commissions earned in the previous month.
            </Typography>
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Typography level="title-sm" sx={{ fontWeight: 700, mb: 0.5 }}>
              When do I get paid?
            </Typography>
            <Typography level="body-md" color="neutral">
              We process payments monthly for all commissions earned in the previous month.
            </Typography>
          </Box>

          <Box>
            <Typography level="title-sm" sx={{ fontWeight: 700, mb: 0.5 }}>
              Is there a minimum payout?
            </Typography>
            <Typography level="body-md" color="neutral">
              Yes, the minimum payout is $25. Once you reach this threshold, we'll process your payment in
              the next payout cycle.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const RegisterSection = () => {
  return (
    <Box component="section" sx={{ textAlign: "center", py: { xs: 6, md: 10 } }}>
      <Typography level="h2" sx={{ fontSize: { xs: 24, sm: 32, md: 40 }, mb: 4, fontWeight: 800, fontFamily: "Libre Baskerville" }}>
        Ready to Start Earning?
      </Typography>

      <Typography
        level="body-lg"
        color="neutral"
        sx={{
          maxWidth: 1100,
          mx: "auto",
          mb: 3,
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          letterSpacing: "0.02em",
        }}
      >
        Join our affiliate program today and start earning passive income.
      </Typography>

      <Button
        size="lg"
        variant="solid"
        sx={{ 
          borderRadius: 14, 
          px: 3.5, 
          backgroundColor: colors.slatePurple, 
          color: 'white', 
          fontWeight: 700,
          '&:hover': {
            backgroundColor: colors.slatePurple,
            opacity: 0.9
          }
        }}
        onClick={() => window.open(AFFILIATE_SIGNUP_URL, "_self")}
      >
        Join Now
      </Button>
    </Box>
  );
};

export const AffiliateProgram = () => {
  return (
    <Dashboard>
      <Helmet>
        <title>Poshwatch - Affiliate Program</title>
        <meta
          name="description"
          content="Join our affiliate program and earn a commission by referring new users to Poshwatch."
        />
        <link rel="canonical" href={`${PATHS.AFFILIATE_PROGRAM}`} />
      </Helmet>
      <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 1.5, sm: 2 } }}>
        <HeroSection />
        <WhyJoinSection />
        <RegisterSection />
      </Box>
    </Dashboard>
  );
};
